// Derives an optional task's Not Exercised / Exercised / Completed tab
// membership from its exercise rows' periods of performance (SAD-341) —
// never from the manually asserted `status`/`exercised` fields on
// OptionalTaskProto, which stay in the schema and edit form for audit only.
//
// Matt's definitions, verbatim from the ticket:
//   NOT EXERCISED — no optional task SLINs/exercised tasks associated at all.
//   EXERCISED — exercised, funded by a SLIN, and CURRENTLY in its period of
//     performance (still being worked on).
//   COMPLETED — has completed its period of performance, but was exercised
//     previously.
// A task with one SLIN still live and another whose period has ended shows
// under BOTH Exercised and Completed — each scoped to only the rows that
// qualify for that tab, per Matt's "each tab shows ONLY the information
// relevant to them."
//
// This module takes only the exercise rows and a reference date — no task —
// which is what makes "the manual flags cannot drive tab membership" a
// structural guarantee rather than a rule callers have to remember to honor.
// Calendar-day comparison done entirely in UTC. A period captured from a
// bare date input is stored at UTC midnight (see dateOrUndefined in
// OptionalTaskExercisesDrawer), so classifying it with local-timezone getters
// lets a behind-UTC process read that UTC midnight boundary as the previous
// local day — SAD-294 was exactly this class of bug in a different corner of
// the repo. Using the UTC getters (not the Date object's raw epoch value, and
// never local getters) makes the classification identical no matter what
// timezone the process runs in.
// Exported so callers that need to key a memo on "the calendar day this
// reference date falls on" (rather than on the Date object's identity, which
// is fresh on every render whenever the caller has no pinned referenceDate)
// share the exact same day boundary this module classifies periods against.
export function utcDayNumber(date) {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}
function periodDayOrUndefined(ts) {
    if (!ts) return undefined;
    const date = ts.toDate();
    if (Number.isNaN(date.getTime())) return undefined;
    return utcDayNumber(date);
}
function classifyPeriod(exercise, referenceDay) {
    const start = periodDayOrUndefined(exercise.popStartDate);
    const end = periodDayOrUndefined(exercise.popEndDate);
    if (start === undefined || end === undefined) return 'unavailable';
    if (referenceDay < start) return 'upcoming';
    if (referenceDay > end) return 'ended';
    return 'active'; // inclusive of both the start and end boundary
}
// Shared display copy so the table's Status column and the exercise drawer's
// heading never drift into two different words for the same derived state.
export const OPTIONAL_TASK_TAB_LABEL = {
    all: 'All',
    not_exercised: 'Not exercised',
    exercised: 'Exercised',
    completed: 'Completed'
};
export const OPTIONAL_TASK_BADGE_LABEL = {
    not_exercised: 'Not exercised',
    exercised: 'Exercised',
    completed: 'Completed',
    upcoming: 'Upcoming',
    period_unavailable: 'Period unavailable'
};
export function deriveOptionalTaskLifecycle(exercises, referenceDate) {
    if (exercises.length === 0) {
        return {
            tabs: new Set([
                'not_exercised'
            ]),
            badgeStatus: 'not_exercised',
            rowsForTab: ()=>[],
            periodUnavailableRows: []
        };
    }
    const referenceDay = utcDayNumber(referenceDate);
    const active = [];
    const ended = [];
    const upcoming = [];
    const periodUnavailableRows = [];
    for (const exercise of exercises){
        const classification = classifyPeriod(exercise, referenceDay);
        if (classification === 'active') active.push(exercise);
        else if (classification === 'ended') ended.push(exercise);
        else if (classification === 'unavailable') periodUnavailableRows.push(exercise);
        else upcoming.push(exercise);
    // 'upcoming' rows qualify for no tab (kept out of Exercised and
    // Completed, same as before) but their dates ARE known, so the badge
    // must say so rather than falsely claiming 'period_unavailable'.
    }
    const tabs = new Set();
    if (active.length > 0) tabs.add('exercised');
    if (ended.length > 0) tabs.add('completed');
    const badgeStatus = active.length > 0 ? 'exercised' : ended.length > 0 ? 'completed' : upcoming.length > 0 ? 'upcoming' : 'period_unavailable';
    const rowsForTab = (tab)=>{
        switch(tab){
            case 'all':
                return exercises;
            case 'exercised':
                return active;
            case 'completed':
                return ended;
            case 'not_exercised':
                return [];
            default:
                return [];
        }
    };
    return {
        tabs,
        badgeStatus,
        rowsForTab,
        periodUnavailableRows
    };
}
